import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './HotelSearch.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/marker-icon-2x.png',
  iconUrl: '/marker-icon.png',
  shadowUrl: '/marker-shadow.png',
});

const HotelSearch = () => {
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  
  // Function to restore search parameters from URL
  const restoreSearchParamsFromURL = () => {
    const restored = {};
    
    // Basic parameters
    if (urlSearchParams.get('cityId')) restored.cityId = parseInt(urlSearchParams.get('cityId'));
    if (urlSearchParams.get('checkInDate')) restored.checkInDate = urlSearchParams.get('checkInDate');
    if (urlSearchParams.get('checkOutDate')) restored.checkOutDate = urlSearchParams.get('checkOutDate');
    if (urlSearchParams.get('language')) restored.language = urlSearchParams.get('language');
    if (urlSearchParams.get('currency')) restored.currency = urlSearchParams.get('currency');
    if (urlSearchParams.get('sortBy')) restored.sortBy = urlSearchParams.get('sortBy');
    if (urlSearchParams.get('maxResult')) restored.maxResult = parseInt(urlSearchParams.get('maxResult'));
    if (urlSearchParams.get('discountOnly')) restored.discountOnly = urlSearchParams.get('discountOnly') === 'true';
    if (urlSearchParams.get('minimumStarRating')) restored.minimumStarRating = parseInt(urlSearchParams.get('minimumStarRating'));
    if (urlSearchParams.get('minimumReviewScore')) restored.minimumReviewScore = parseFloat(urlSearchParams.get('minimumReviewScore'));
    if (urlSearchParams.get('dailyRateMin')) restored.dailyRateMin = parseInt(urlSearchParams.get('dailyRateMin'));
    if (urlSearchParams.get('dailyRateMax')) restored.dailyRateMax = parseInt(urlSearchParams.get('dailyRateMax'));
    
    // Occupancy parameters
    const numberOfAdult = urlSearchParams.get('numberOfAdult');
    const numberOfChildren = urlSearchParams.get('numberOfChildren');
    if (numberOfAdult || numberOfChildren) {
      restored.occupancy = {
        numberOfAdult: numberOfAdult ? parseInt(numberOfAdult) : 2,
        numberOfChildren: numberOfChildren ? parseInt(numberOfChildren) : 0
      };
    }
    
    return restored;
  };
  
  // Function to restore children ages from URL
  const restoreChildrenAgesFromURL = () => {
    const childrenAgesParam = urlSearchParams.get('childrenAges');
    if (childrenAgesParam) {
      try {
        return JSON.parse(childrenAgesParam);
      } catch (e) {
        return [];
      }
    }
    return [];
  };
  
  // Function to convert search parameters to URL search params
  const createSearchParamsString = (params) => {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (key === 'occupancy') {
          // Handle occupancy object
          urlParams.append('numberOfAdult', value.numberOfAdult);
          urlParams.append('numberOfChildren', value.numberOfChildren);
        } else if (Array.isArray(value)) {
          // Handle arrays (like childrenAges)
          urlParams.append(key, JSON.stringify(value));
        } else {
          urlParams.append(key, value.toString());
        }
      }
    });
    return urlParams.toString();
  };

  // Function to handle view details navigation with search params
  const handleViewDetails = (hotelId) => {
    const searchParamsString = createSearchParamsString({
      ...searchParams,
      childrenAges: childrenAges
    });
    navigate(`/hotel/${hotelId}?${searchParamsString}`);
  };

  // Default search parameters
  const defaultSearchParams = {
    cityId: 9395,
    checkInDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    checkOutDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    language: 'en-us',
    currency: 'USD',
    sortBy: 'Recommended',
    maxResult: 10,
    discountOnly: false,
    minimumStarRating: 0,
    minimumReviewScore: 0,
    dailyRateMin: 0,
    dailyRateMax: 100000,
    occupancy: {
      numberOfAdult: 2,
      numberOfChildren: 0,
      childrenAges: []
    }
  };

  // Merge default params with URL params
  const [searchParams, setSearchParams] = useState(() => ({
    ...defaultSearchParams,
    ...restoreSearchParamsFromURL()
  }));

  const [childrenAges, setChildrenAges] = useState(() => restoreChildrenAgesFromURL());
  
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [mapCenter, setMapCenter] = useState([13.7563, 100.5018]); // Bangkok default
  const [locationDetected, setLocationDetected] = useState(false);

  // Detect user's location and set default language/currency
  useEffect(() => {
    const detectLocationAndSetDefaults = async () => {
      try {
        // Try to get user's location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              
              // Reverse geocoding to detect country (simplified approach)
              fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`)
                .then(response => response.json())
                .then(data => {
                  const countryCode = data.countryCode;
                  const { language, currency } = getDefaultsForCountry(countryCode);
                  
                  setSearchParams(prev => ({
                    ...prev,
                    language,
                    currency
                  }));
                  
                  setLocationDetected(true);
                })
                .catch(error => {
                  console.log('Geocoding failed, using defaults');
                  setLocationDetected(true);
                });
            },
            (error) => {
              console.log('Geolocation failed, using defaults');
              setLocationDetected(true);
            }
          );
        } else {
          console.log('Geolocation not supported');
          setLocationDetected(true);
        }
      } catch (error) {
        console.log('Location detection failed, using defaults');
        setLocationDetected(true);
      }
    };

    detectLocationAndSetDefaults();
  }, []);

  // Auto-search when page loads with search parameters
  useEffect(() => {
    const hasSearchParams = urlSearchParams.toString().length > 0;
    const hasMinimumRequiredParams = searchParams.cityId && searchParams.checkInDate && searchParams.checkOutDate;
    
    if (hasSearchParams && hasMinimumRequiredParams && !searchPerformed && !loading) {
      // Auto-trigger search if we have URL parameters and minimum required fields
      console.log('Auto-triggering search with parameters:', searchParams);
      fetchHotels();
    }
  }, [urlSearchParams.toString(), searchParams.cityId, searchParams.checkInDate, searchParams.checkOutDate, searchPerformed, loading]);

  // Update URL when search parameters change (for browser history)
  useEffect(() => {
    if (searchPerformed) {
      const searchParamsString = createSearchParamsString({
        ...searchParams,
        childrenAges: childrenAges
      });
      
      // Only update URL if it's different from current
      if (searchParamsString !== urlSearchParams.toString()) {
        window.history.replaceState({}, '', `${window.location.pathname}?${searchParamsString}`);
      }
    }
  }, [searchParams, childrenAges, searchPerformed]);

  // Function to get default language and currency based on country
  const getDefaultsForCountry = (countryCode) => {
    const countryDefaults = {
      'US': { language: 'en-us', currency: 'USD' },
      'CA': { language: 'en-ca', currency: 'CAD' },
      'GB': { language: 'en-gb', currency: 'GBP' },
      'AU': { language: 'en-au', currency: 'AUD' },
      'NZ': { language: 'en-nz', currency: 'NZD' },
      'SG': { language: 'en-sg', currency: 'SGD' },
      'HK': { language: 'zh-hk', currency: 'HKD' },
      'TH': { language: 'th-th', currency: 'THB' },
      'JP': { language: 'ja-jp', currency: 'JPY' },
      'KR': { language: 'ko-kr', currency: 'KRW' },
      'CN': { language: 'zh-cn', currency: 'CNY' },
      'TW': { language: 'zh-tw', currency: 'TWD' },
      'FR': { language: 'fr-fr', currency: 'EUR' },
      'DE': { language: 'de-de', currency: 'EUR' },
      'IT': { language: 'it-it', currency: 'EUR' },
      'ES': { language: 'es-es', currency: 'EUR' },
      'NL': { language: 'nl-nl', currency: 'EUR' },
      'BE': { language: 'nl-be', currency: 'EUR' },
      'RU': { language: 'ru-ru', currency: 'RUB' },
      'IN': { language: 'en-in', currency: 'INR' },
      'MY': { language: 'ms-my', currency: 'MYR' },
      'ID': { language: 'id-id', currency: 'IDR' },
      'VN': { language: 'vi-vn', currency: 'VND' },
      'PH': { language: 'en-us', currency: 'PHP' },
      'AE': { language: 'ar-ae', currency: 'AED' },
      'SA': { language: 'ar-ae', currency: 'SAR' },
      'ZA': { language: 'en-za', currency: 'ZAR' }
    };
    
    return countryDefaults[countryCode] || { language: 'en-us', currency: 'USD' };
  };

  // Comprehensive city data with popular destinations
  const cities = [
    // { id: 12345, name: 'Puerto Escondido, Mexico', country: 'Mexico' },
    { id: 9395, name: 'Bangkok, Thailand', country: 'Thailand', lat: 13.7563, lng: 100.5018 },
    // { id: 298, name: 'Tokyo, Japan', country: 'Japan' },
    // { id: 17193, name: 'New York, USA', country: 'United States' },
    // { id: 6667, name: 'London, UK', country: 'United Kingdom' },
    // { id: 16552, name: 'Paris, France', country: 'France' },
    // { id: 1642, name: 'Singapore, Singapore', country: 'Singapore' },
    // { id: 14552, name: 'Dubai, UAE', country: 'United Arab Emirates' },
    // { id: 8048, name: 'Sydney, Australia', country: 'Australia' },
    // { id: 11461, name: 'Rome, Italy', country: 'Italy' }
  ];

  // Create custom map markers with price
  const createCustomIcon = (price, currency) => {
    const currencySymbol = getCurrencySymbol(currency);
    return L.divIcon({
      html: `<div class="custom-marker">
               <div class="marker-price">${currencySymbol}${price}</div>
               <div class="marker-pointer"></div>
             </div>`,
      className: 'custom-marker-container',
      iconSize: [80, 40],
      iconAnchor: [40, 40],
      popupAnchor: [0, -40]
    });
  };

  // Sort options based on API schema
  const sortOptions = [
    { value: 'Recommended', label: 'Recommended' },
    { value: 'PriceAsc', label: 'Price: Low to High' },
    { value: 'PriceDesc', label: 'Price: High to Low' },
    { value: 'StarRatingDesc', label: 'Star Rating: High to Low' },
    { value: 'StarRatingAsc', label: 'Star Rating: Low to High' },
    { value: 'AllGuestsReviewScore', label: 'Guest Rating' },
    { value: 'BusinessTravellerReviewScore', label: 'Business Traveller Rating' },
    { value: 'CouplesReviewScore', label: 'Couples Rating' },
    { value: 'SoloTravllersReviewScore', label: 'Solo Travellers Rating' },
    { value: 'FamiliesWithYoungReviewScore', label: 'Families with Young Kids' },
    { value: 'FamiliesWithTeenReviewScore', label: 'Families with Teens' },
    { value: 'GroupsReviewScore', label: 'Groups Rating' }
  ];

  // Language options based on Agoda API
  const languageOptions = [
    { value: 'en-us', label: 'English (US)' },
    { value: 'fr-fr', label: 'Français' },
    { value: 'de-de', label: 'Deutsch' },
    { value: 'it-it', label: 'Italiano' },
    { value: 'es-es', label: 'Español' },
    { value: 'ja-jp', label: '日本語' },
    { value: 'zh-hk', label: '繁體中文 (香港)' },
    { value: 'zh-cn', label: '简体中文' },
    { value: 'ko-kr', label: '한국어' },
    { value: 'el-gr', label: 'Ελληνικά' },
    { value: 'ru-ru', label: 'Русский' },
    { value: 'pt-pt', label: 'Português' },
    { value: 'nl-nl', label: 'Nederlands' },
    { value: 'en-ca', label: 'English (Canada)' },
    { value: 'en-in', label: 'English (India)' },
    { value: 'en-gb', label: 'English (UK)' },
    { value: 'en-za', label: 'English (South Africa)' },
    { value: 'en-au', label: 'English (Australia)' },
    { value: 'en-sg', label: 'English (Singapore)' },
    { value: 'zh-tw', label: '繁體中文 (台灣)' },
    { value: 'en-nz', label: 'English (New Zealand)' },
    { value: 'th-th', label: 'ไทย' },
    { value: 'ms-my', label: 'Bahasa Malaysia' },
    { value: 'vi-vn', label: 'Tiếng Việt' },
    { value: 'sv-se', label: 'Svenska' },
    { value: 'id-id', label: 'Bahasa Indonesia' },
    { value: 'pl-pl', label: 'Polski' },
    { value: 'nb-no', label: 'Norsk' },
    { value: 'da-dk', label: 'Dansk' },
    { value: 'fi-fi', label: 'Suomi' },
    { value: 'cs-cz', label: 'Čeština' },
    { value: 'tr-tr', label: 'Türkçe' },
    { value: 'ca-es', label: 'Català' },
    { value: 'hu-hu', label: 'Magyar' },
    { value: 'hi-in', label: 'हिन्दी' },
    { value: 'bg-bg', label: 'Български' },
    { value: 'ro-ro', label: 'Română' },
    { value: 'sl-si', label: 'Slovenščina' },
    { value: 'he-il', label: 'עברית' },
    { value: 'ar-ae', label: 'العربية' },
    { value: 'nl-be', label: 'Nederlands (België)' },
    { value: 'en-ie', label: 'English (Ireland)' },
    { value: 'pt-br', label: 'Português (Brasil)' },
    { value: 'es-ar', label: 'Español (Argentina)' },
    { value: 'es-mx', label: 'Español (México)' },
    { value: 'lt-lt', label: 'Lietuvių' },
    { value: 'lv-lv', label: 'Latviešu' },
    { value: 'hr-hr', label: 'Hrvatski' },
    { value: 'et-ee', label: 'Eesti' },
    { value: 'uk-ua', label: 'Українська' }
  ];

  // Currency options based on Agoda API
  const currencyOptions = [
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'EUR', label: 'EUR (€)', symbol: '€' },
    { value: 'GBP', label: 'GBP (£)', symbol: '£' },
    { value: 'HKD', label: 'HKD (HK$)', symbol: 'HK$' },
    { value: 'MYR', label: 'MYR (RM)', symbol: 'RM' },
    { value: 'SGD', label: 'SGD (S$)', symbol: 'S$' },
    { value: 'THB', label: 'THB (฿)', symbol: '฿' },
    { value: 'NZD', label: 'NZD (NZ$)', symbol: 'NZ$' },
    { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
    { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
    { value: 'ZAR', label: 'ZAR (R)', symbol: 'R' },
    { value: 'CAD', label: 'CAD (C$)', symbol: 'C$' },
    { value: 'AED', label: 'AED (د.إ)', symbol: 'د.إ' },
    { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
    { value: 'PHP', label: 'PHP (₱)', symbol: '₱' },
    { value: 'CHF', label: 'CHF (Fr)', symbol: 'Fr' },
    { value: 'DKK', label: 'DKK (kr)', symbol: 'kr' },
    { value: 'SEK', label: 'SEK (kr)', symbol: 'kr' },
    { value: 'CZK', label: 'CZK (Kč)', symbol: 'Kč' },
    { value: 'PLN', label: 'PLN (zł)', symbol: 'zł' },
    { value: 'IDR', label: 'IDR (Rp)', symbol: 'Rp' },
    { value: 'KRW', label: 'KRW (₩)', symbol: '₩' },
    { value: 'INR', label: 'INR (₹)', symbol: '₹' },
    { value: 'TWD', label: 'TWD (NT$)', symbol: 'NT$' },
    { value: 'NOK', label: 'NOK (kr)', symbol: 'kr' },
    { value: 'OMR', label: 'OMR (ر.ع.)', symbol: 'ر.ع.' },
    { value: 'FJD', label: 'FJD (FJ$)', symbol: 'FJ$' },
    { value: 'BHD', label: 'BHD (.د.ب)', symbol: '.د.ب' },
    { value: 'ARS', label: 'ARS ($)', symbol: '$' },
    { value: 'XPF', label: 'XPF (₣)', symbol: '₣' },
    { value: 'VND', label: 'VND (₫)', symbol: '₫' },
    { value: 'HUF', label: 'HUF (Ft)', symbol: 'Ft' },
    { value: 'UAH', label: 'UAH (₴)', symbol: '₴' },
    { value: 'JOD', label: 'JOD (د.ا)', symbol: 'د.ا' },
    { value: 'KWD', label: 'KWD (د.ك)', symbol: 'د.ك' },
    { value: 'MXN', label: 'MXN ($)', symbol: '$' },
    { value: 'NGN', label: 'NGN (₦)', symbol: '₦' },
    { value: 'ILS', label: 'ILS (₪)', symbol: '₪' },
    { value: 'PKR', label: 'PKR (₨)', symbol: '₨' },
    { value: 'QAR', label: 'QAR (ر.ق)', symbol: 'ر.ق' },
    { value: 'RUB', label: 'RUB (₽)', symbol: '₽' },
    { value: 'SAR', label: 'SAR (ر.س)', symbol: 'ر.س' },
    { value: 'KZT', label: 'KZT (₸)', symbol: '₸' },
    { value: 'TRY', label: 'TRY (₺)', symbol: '₺' },
    { value: 'BGN', label: 'BGN (лв)', symbol: 'лв' },
    { value: 'RON', label: 'RON (lei)', symbol: 'lei' },
    { value: 'KHR', label: 'KHR (៛)', symbol: '៛' },
    { value: 'LAK', label: 'LAK (₭)', symbol: '₭' },
    { value: 'LKR', label: 'LKR (Rs)', symbol: 'Rs' },
    { value: 'MVR', label: 'MVR (Rf)', symbol: 'Rf' },
    { value: 'EGP', label: 'EGP (£)', symbol: '£' }
  ];

  const fetchHotels = async () => {
    setLoading(true);
    try {
      // Update map center based on selected city
      const selectedCity = cities.find(city => city.id === parseInt(searchParams.cityId));
      if (selectedCity && selectedCity.lat && selectedCity.lng) {
        setMapCenter([selectedCity.lat, selectedCity.lng]);
      }

      const requestBody = {
        criteria: {
          additional: {
            currency: searchParams.currency,
            language: searchParams.language,
            maxResult: searchParams.maxResult,
            sortBy: searchParams.sortBy,
            discountOnly: searchParams.discountOnly,
            minimumStarRating: searchParams.minimumStarRating,
            minimumReviewScore: searchParams.minimumReviewScore,
            dailyRate: {
              minimum: searchParams.dailyRateMin,
              maximum: searchParams.dailyRateMax
            }
          },
          checkInDate: searchParams.checkInDate,
          checkOutDate: searchParams.checkOutDate,
          cityId: parseInt(searchParams.cityId),
          occupancy: {
            numberOfAdult: searchParams.occupancy.numberOfAdult,
            numberOfChildren: searchParams.occupancy.numberOfChildren,
            ...(searchParams.occupancy.numberOfChildren > 0 && {
              childrenAges: searchParams.occupancy.childrenAges
            })
          }
        }
      };

      const response = await fetch('https://7juj2p3or5.execute-api.ap-southeast-7.amazonaws.com/Prod/agoda', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip,deflate',
          'Authorization': '1948265:bfd722e6-8732-4342-bbd1-825d24cb33c9',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setHotels(data.results || []);
      setSearchPerformed(true);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      // Set mock data for demonstration
      setHotels(mockHotelData);
      setSearchPerformed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHotels();
  };

  const handleInputChange = (field, value) => {
    if (field.includes('occupancy.')) {
      const occupancyField = field.split('.')[1];
      setSearchParams(prev => ({
        ...prev,
        occupancy: {
          ...prev.occupancy,
          [occupancyField]: value
        }
      }));
    } else {
      setSearchParams(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleChildrenCountChange = (count) => {
    const newChildrenAges = Array(parseInt(count)).fill(10);
    setChildrenAges(newChildrenAges);
    setSearchParams(prev => ({
      ...prev,
      occupancy: {
        ...prev.occupancy,
        numberOfChildren: parseInt(count),
        childrenAges: newChildrenAges
      }
    }));
  };

  const handleChildAgeChange = (index, age) => {
    const newAges = [...childrenAges];
    newAges[index] = parseInt(age);
    setChildrenAges(newAges);
    setSearchParams(prev => ({
      ...prev,
      occupancy: {
        ...prev.occupancy,
        childrenAges: newAges
      }
    }));
  };

  const handleAdvancedFilterChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCurrencySymbol = (currency) => {
    const currencyData = currencyOptions.find(option => option.value === currency);
    return currencyData ? currencyData.symbol : currency;
  };

  const getReviewScoreText = (score) => {
    if (score >= 9) return 'Exceptional';
    if (score >= 8) return 'Excellent';
    if (score >= 7) return 'Very Good';
    if (score >= 6) return 'Good';
    if (score >= 5) return 'Fair';
    return 'Poor';
  };

  const getReviewScoreColor = (score) => {
    if (score >= 9) return '#0066cc';
    if (score >= 8) return '#00aa00';
    if (score >= 7) return '#669900';
    if (score >= 6) return '#cc9900';
    if (score >= 5) return '#ff6600';
    return '#cc0000';
  };

  // Enhanced mock data for demonstration when API fails
  const mockHotelData = [
    {
      hotelId: 1,
      hotelName: "Bangkok Palace Hotel",
      starRating: 5,
      reviewScore: 8.5,
      reviewCount: 1250,
      dailyRate: 120,
      crossedOutRate: 150,
      currency: "USD",
      discountPercentage: 20,
      imageURL: "/logo.jpeg",
      landingURL: "https://agoda.com",
      freeWifi: true,
      includeBreakfast: true,
      latitude: 13.7563,
      longitude: 100.5018
    },
    {
      hotelId: 2,
      hotelName: "Riverside Resort Bangkok",
      starRating: 4,
      reviewScore: 7.8,
      reviewCount: 890,
      dailyRate: 85,
      crossedOutRate: 0,
      currency: "USD",
      discountPercentage: 0,
      imageURL: "/logo.jpeg",
      landingURL: "https://agoda.com",
      freeWifi: true,
      includeBreakfast: false,
      latitude: 13.7400,
      longitude: 100.5100
    },
    {
      hotelId: 3,
      hotelName: "Modern City Center Hotel",
      starRating: 4,
      reviewScore: 8.2,
      reviewCount: 650,
      dailyRate: 95,
      crossedOutRate: 110,
      currency: "USD",
      discountPercentage: 15,
      imageURL: "/logo.jpeg",
      landingURL: "https://agoda.com",
      freeWifi: true,
      includeBreakfast: true,
      latitude: 13.7650,
      longitude: 100.4950
    }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>★</span>
    ));
  };

  const calculateNights = () => {
    const checkIn = new Date(searchParams.checkInDate);
    const checkOut = new Date(searchParams.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Handle view photos functionality
  const handleViewPhotos = (hotel, e) => {
    e.stopPropagation(); // Prevent event bubbling
    e.preventDefault();
    
    // For now, redirect to the hotel detail page where photos are displayed
    handleViewDetails(hotel.hotelId);
    
    // Alternative: You could implement a photo gallery modal here
    // console.log('View photos for:', hotel.hotelName);
  };

  return (
    <div className="hotel-search-container">
      <div className="search-header">
        <div className="search-hero">
          <h1>🏨 Discover Amazing Hotels</h1>
          <p>Find and compare the best hotel deals from Us</p>
          {/* <div className="search-stats">
            <span>✨ Over 2 million properties</span>
            <span>🌍 190+ countries</span>
            <span>💝 Best price guarantee</span>
          </div> */}
        </div>
      </div>

      {/* Enhanced Search Form */}
      <form onSubmit={handleSearch} className="search-form">
        <div className="form-card">
          <div className="form-header">
            <h3>🔍 Search Hotels</h3>
            {locationDetected && (
              <div className="location-indicator">
                <span className="location-icon">📍</span>
                <span className="location-text">Location detected - defaults set</span>
              </div>
            )}
          </div>
          
          <div className="search-row primary">
            <div className="form-group destination">
              <label>🏙️ Destination</label>
              <select 
                value={searchParams.cityId} 
                onChange={(e) => handleInputChange('cityId', e.target.value)}
                className="destination-select"
              >
                {cities.map(city => (
                  <option key={city.id} value={city.id}>
                    {city.name}, {city.country}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>📅 Check-in</label>
              <input 
                type="date" 
                value={searchParams.checkInDate}
                onChange={(e) => handleInputChange('checkInDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label>📅 Check-out</label>
              <input 
                type="date" 
                value={searchParams.checkOutDate}
                onChange={(e) => handleInputChange('checkOutDate', e.target.value)}
                min={searchParams.checkInDate}
              />
            </div>
          </div>

          <div className="search-row secondary">
            <div className="form-group guests">
              <label>👥 Guests</label>
              <div className="guests-grid">
                <select 
                  value={searchParams.occupancy.numberOfAdult} 
                  onChange={(e) => handleInputChange('occupancy.numberOfAdult', parseInt(e.target.value))}
                  title="Adults"
                >
                  {[1,2,3,4,5,6,7,8].map(num => (
                    <option key={num} value={num}>{num} Adult{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
                <select 
                  value={searchParams.occupancy.numberOfChildren} 
                  onChange={(e) => handleChildrenCountChange(e.target.value)}
                  title="Children"
                >
                  {[0,1,2,3,4].map(num => (
                    <option key={num} value={num}>{num} Child{num !== 1 ? 'ren' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group language-currency">
              <label>🌐 Language</label>
              <select 
                value={searchParams.language} 
                onChange={(e) => handleInputChange('language', e.target.value)}
                className="language-select"
                title="Select Language"
              >
                {languageOptions.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group language-currency">
              <label>💱 Currency</label>
              <select 
                value={searchParams.currency} 
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="currency-select"
                title="Select Currency"
              >
                {currencyOptions.map(curr => (
                  <option key={curr.value} value={curr.value}>
                    {curr.label}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit" className="search-btn primary" disabled={loading}>
              {loading ? (
                <span className="loading-text">
                  <span className="spinner"></span>
                  Searching...
                </span>
              ) : (
                <span>🔍 Search Hotels</span>
              )}
            </button>
          </div>

          {/* Children Ages Input */}
          {searchParams.occupancy.numberOfChildren > 0 && (
            <div className="children-ages">
              <label>👶 Children Ages</label>
              <div className="ages-grid">
                {Array.from({ length: searchParams.occupancy.numberOfChildren }, (_, index) => (
                  <select
                    key={index}
                    value={childrenAges[index] || 10}
                    onChange={(e) => handleChildAgeChange(index, e.target.value)}
                    className="age-select"
                  >
                    {Array.from({ length: 18 }, (_, age) => (
                      <option key={age} value={age}>{age} years old</option>
                    ))}
                  </select>
                ))}
              </div>
            </div>
          )}

          {/* Advanced Filters Toggle */}
          <div className="advanced-filters-toggle">
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="toggle-btn"
            >
              {showAdvancedFilters ? '🔼 Hide' : '🔽 Show'} Advanced Filters
            </button>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="advanced-filters">
              <div className="filters-grid">
                {/* <div className="filter-group">
                  <label>💰 Currency</label>
                  <select
                    value={searchParams.currency}
                    onChange={(e) => handleAdvancedFilterChange('currency', e.target.value)}
                    className="filter-select"
                  >
                    {currencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div> */}

                <div className="filter-group">
                  <label>📊 Sort By</label>
                  <select
                    value={searchParams.sortBy}
                    onChange={(e) => handleAdvancedFilterChange('sortBy', e.target.value)}
                    className="filter-select"
                  >
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>⭐ Min Star Rating</label>
                  <select
                    value={searchParams.minimumStarRating}
                    onChange={(e) => handleAdvancedFilterChange('minimumStarRating', parseFloat(e.target.value))}
                    className="filter-select"
                  >
                    <option value={0}>Any</option>
                    <option value={1}>1+ Stars</option>
                    <option value={2}>2+ Stars</option>
                    <option value={3}>3+ Stars</option>
                    <option value={4}>4+ Stars</option>
                    <option value={5}>5 Stars</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>📊 Min Review Score</label>
                  <select
                    value={searchParams.minimumReviewScore}
                    onChange={(e) => handleAdvancedFilterChange('minimumReviewScore', parseFloat(e.target.value))}
                    className="filter-select"
                  >
                    <option value={0}>Any</option>
                    <option value={6}>6.0+ Fair</option>
                    <option value={7}>7.0+ Good</option>
                    <option value={8}>8.0+ Excellent</option>
                    <option value={9}>9.0+ Exceptional</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>💵 Min Price ({getCurrencySymbol(searchParams.currency)})</label>
                  <input
                    type="number"
                    value={searchParams.dailyRateMin}
                    onChange={(e) => handleAdvancedFilterChange('dailyRateMin', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    min="0"
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label>💵 Max Price ({getCurrencySymbol(searchParams.currency)})</label>
                  <input
                    type="number"
                    value={searchParams.dailyRateMax}
                    onChange={(e) => handleAdvancedFilterChange('dailyRateMax', parseFloat(e.target.value) || 100000)}
                    placeholder="100000"
                    min="0"
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label>🏷️ Results Count</label>
                  <select
                    value={searchParams.maxResult}
                    onChange={(e) => handleAdvancedFilterChange('maxResult', parseInt(e.target.value))}
                    className="filter-select"
                  >
                    <option value={5}>5 results</option>
                    <option value={10}>10 results</option>
                    <option value={20}>20 results</option>
                    <option value={30}>30 results</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={searchParams.discountOnly}
                      onChange={(e) => handleAdvancedFilterChange('discountOnly', e.target.checked)}
                    />
                    🏷️ Show only discounted hotels
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </form>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-animation">
            <div className="hotel-icon">🏨</div>
            <div className="loading-bars">
              <div className="bar"></div>
              <div className="bar"></div>
              <div className="bar"></div>
            </div>
          </div>
          <h3>Finding the perfect hotels for you...</h3>
          <p>Searching through millions of properties worldwide</p>
        </div>
      )}

      {/* Results Section */}
      {!loading && searchPerformed && (
        <div className="results-container">
          <div className="results-header">
            <div className="results-info">
              <h2>
                {hotels.length} hotel{hotels.length !== 1 ? 's' : ''} found
              </h2>
              <p>
                {calculateNights()} night{calculateNights() !== 1 ? 's' : ''} • 
                {searchParams.occupancy.numberOfAdult} adult{searchParams.occupancy.numberOfAdult > 1 ? 's' : ''} • 
                {searchParams.occupancy.numberOfChildren} child{searchParams.occupancy.numberOfChildren !== 1 ? 'ren' : ''}
              </p>
            </div>
            <div className="view-controls">
              <div className="view-toggle">
                <button 
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  📋 List View
                </button>
                <button 
                  className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
                  onClick={() => setViewMode('map')}
                >
                  🗺️ Map View
                </button>
              </div>
              <div className="sort-info">
                <span className="sort-label">Sorted by: {searchParams.sortBy}</span>
              </div>
            </div>
          </div>

          {viewMode === 'list' ? (
            <div className="hotels-grid">
              {hotels.map(hotel => (
                <div key={hotel.hotelId} className="hotel-card modern">
                  <div className="hotel-image-container">
                    <img src={hotel.imageURL} alt={hotel.hotelName} className="hotel-image" />
                    {hotel.discountPercentage > 0 && (
                      <div className="discount-badge">
                        <span className="discount-icon">🏷️</span>
                        -{hotel.discountPercentage}% OFF
                      </div>
                    )}
                    <div className="image-overlay">
                      <button 
                        className="view-photos-btn"
                        onClick={(e) => handleViewPhotos(hotel, e)}
                      >
                        📸 View Photos
                      </button>
                    </div>
                  </div>

                  <div className="hotel-content">
                    <div className="hotel-header">
                      <div className="hotel-title-section">
                        <h3 className="hotel-name">{hotel.hotelName}</h3>
                        <div className="star-rating">
                          {renderStars(hotel.starRating)}
                          <span className="star-text">
                            {hotel.starRating > 0 ? `${hotel.starRating} Star${hotel.starRating > 1 ? 's' : ''}` : 'Unrated'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="hotel-amenities">
                      {hotel.freeWifi && (
                        <span className="amenity wifi">
                          <span className="amenity-icon">📶</span>
                          Free WiFi
                        </span>
                      )}
                      {hotel.includeBreakfast && (
                        <span className="amenity breakfast">
                          <span className="amenity-icon">🍳</span>
                          Breakfast Included
                        </span>
                      )}
                    </div>

                    <div className="hotel-rating-section">
                      <div className="rating-badge" style={{ backgroundColor: getReviewScoreColor(hotel.reviewScore) }}>
                        <span className="rating-score">{hotel.reviewScore.toFixed(1)}</span>
                      </div>
                      <div className="rating-details">
                        <span className="rating-text">{getReviewScoreText(hotel.reviewScore)}</span>
                        <span className="review-count">
                          {hotel.reviewCount.toLocaleString()} review{hotel.reviewCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="hotel-pricing-section">
                      <div className="price-container">
                        {hotel.crossedOutRate > 0 && (
                          <div className="original-price">
                            <span className="crossed-price">
                              {getCurrencySymbol(hotel.currency)}{hotel.crossedOutRate.toLocaleString()}
                            </span>
                          </div>
                        )}
                        <div className="current-price">
                          <span className="price-amount">
                            {getCurrencySymbol(hotel.currency)}{hotel.dailyRate.toLocaleString()}
                          </span>
                          <span className="price-period">per night</span>
                        </div>
                        <div className="total-price">
                          <strong>
                            Total: {getCurrencySymbol(hotel.currency)}{(hotel.dailyRate * calculateNights()).toLocaleString()}
                          </strong>
                          <span className="tax-info">includes taxes & fees</span>
                        </div>
                      </div>

                      <div className="hotel-action-buttons">
                        <button 
                          onClick={() => handleViewDetails(hotel.hotelId)}
                          className="view-details-btn"
                        >
                          <span className="btn-icon">👁️</span>
                          View Details
                        </button>
                        
                        <a 
                          href={hotel.landingURL} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="book-now-btn modern"
                        >
                          <span className="btn-icon">🔗</span>
                          View on Agoda
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="map-container">
              <MapContainer 
                center={mapCenter} 
                zoom={13} 
                style={{ height: '600px', width: '100%' }}
                className="hotel-map"
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {hotels.map(hotel => {
                  // Use mock coordinates if real coordinates are not available
                  const lat = hotel.latitude || (mapCenter[0] + (Math.random() - 0.5) * 0.02);
                  const lng = hotel.longitude || (mapCenter[1] + (Math.random() - 0.5) * 0.02);
                  
                  return (
                    <Marker
                      key={hotel.hotelId}
                      position={[lat, lng]}
                      icon={createCustomIcon(hotel.dailyRate, hotel.currency)}
                    >
                      <Popup className="hotel-popup">
                        <div className="popup-content">
                          <div className="popup-header">
                            <h4>{hotel.hotelName}</h4>
                            <div className="popup-stars">
                              {renderStars(hotel.starRating)}
                            </div>
                          </div>
                          
                          <div className="popup-image">
                            <img src={hotel.imageURL} alt={hotel.hotelName} />
                          </div>
                          
                          <div className="popup-details">
                            <div className="popup-rating">
                              <span className="rating-score" style={{ backgroundColor: getReviewScoreColor(hotel.reviewScore) }}>
                                {hotel.reviewScore.toFixed(1)}
                              </span>
                              <span className="rating-text">{getReviewScoreText(hotel.reviewScore)}</span>
                            </div>
                            
                            <div className="popup-price">
                              {hotel.crossedOutRate > 0 && (
                                <div className="crossed-price">
                                  {getCurrencySymbol(hotel.currency)}{hotel.crossedOutRate}
                                </div>
                              )}
                              <div className="current-price">
                                <strong>{getCurrencySymbol(hotel.currency)}{hotel.dailyRate}</strong>
                                <span>/night</span>
                              </div>
                            </div>
                            
                            <div className="popup-amenities">
                              {hotel.freeWifi && <span className="amenity">📶 WiFi</span>}
                              {hotel.includeBreakfast && <span className="amenity">🍳 Breakfast</span>}
                            </div>
                            
                            <div className="popup-buttons">
                              <button 
                                onClick={() => handleViewDetails(hotel.hotelId)}
                                className="popup-details-btn"
                              >
                                👁️ View Details
                              </button>
                              <a 
                                href={hotel.landingURL} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="popup-book-btn"
                              >
                                🔗 Book Now
                              </a>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          )}

          {hotels.length === 0 && (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h3>No hotels found</h3>
              <p>Try adjusting your search criteria or filters</p>
              <div className="suggestions">
                <h4>Suggestions:</h4>
                <ul>
                  <li>Try different dates</li>
                  <li>Remove some filters</li>
                  <li>Search for a different destination</li>
                  <li>Increase your budget range</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Stats Footer */}
      {searchPerformed && !loading && hotels.length > 0 && (
        <div className="search-stats-footer">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">{hotels.length}</span>
              <span className="stat-label">Hotels Found</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {getCurrencySymbol(searchParams.currency)}
                {Math.min(...hotels.map(h => h.dailyRate))}
              </span>
              <span className="stat-label">Lowest Price</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {Math.max(...hotels.map(h => h.reviewScore)).toFixed(1)}
              </span>
              <span className="stat-label">Highest Rating</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {hotels.filter(h => h.discountPercentage > 0).length}
              </span>
              <span className="stat-label">With Discounts</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelSearch;
